import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { type DetailedPoliceReport } from '@/services/api/generated/policeReportsService';

import { PoliceEmptyStateWithWorkflow } from './PoliceEmptyStateWithWorkflow';
import { PoliceReportsList } from './PoliceReportsList';

interface PoliceContentProps {
  isLoading: boolean;
  error: boolean;
  policeReports?: DetailedPoliceReport[];
  matterId: string;
  onReportSelect: (reportId: string) => void;
  onCreateReport: () => void;
  onRefetch: () => void;
  firmRequiresFoia?: boolean;
}

const PoliceReportCardSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-start gap-4">
      <Skeleton className="w-12 h-12 rounded" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <div className="flex gap-2 mt-3">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
      </div>
    </div>
  </div>
);

const PoliceContentSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200">
    <div className="p-4 border-b border-gray-200">
      <Skeleton className="h-6 w-48" />
    </div>
    <div className="p-4 space-y-3">
      <PoliceReportCardSkeleton />
      <PoliceReportCardSkeleton />
    </div>
  </div>
);

export const PoliceContent: React.FC<PoliceContentProps> = ({
  isLoading,
  error,
  policeReports,
  matterId,
  onReportSelect,
  onCreateReport,
  onRefetch,
  firmRequiresFoia,
}) => {
  if (isLoading) {
    return <PoliceContentSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-red-600 mb-3">Failed to load reports</p>
        <button
          type="button"
          onClick={onRefetch}
          className="text-sm text-blue-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!policeReports || policeReports.length === 0) {
    return (
      <PoliceEmptyStateWithWorkflow
        matterId={matterId}
        onCreateReport={onCreateReport}
      />
    );
  }

  return (
    <PoliceReportsList
      reports={policeReports}
      matterId={matterId}
      onReportSelect={onReportSelect}
      firmRequiresFoia={firmRequiresFoia}
    />
  );
};
